/**
 * npeContractsApi — RTK Query slice cho /purchase/npe-contracts/*.
 * Tag types: 'NPEContract' (list + detail).
 *
 * **Backend dùng `contract_no` (NPE-XXXXX) làm path key, không phải numeric id.**
 *   GET    /purchase/npe-contracts/list?...
 *   GET    /purchase/npe-contracts/suggest-contract-no
 *   POST   /purchase/npe-contracts/create
 *   GET    /purchase/npe-contracts/{contract_no}
 *   PUT    /purchase/npe-contracts/{contract_no}
 *   DELETE /purchase/npe-contracts/{contract_no}
 *   POST   /purchase/npe-contracts/{contract_no}/copy
 *   GET    /purchase/npe-contracts/{contract_no}/print
 */
import { safePathEncode } from '@/utils/safePath'
import { baseApi } from './baseApi'
import type {
  DataResponse,
  LocalPPrintResult,
  MessageResponse,
  NPEContractCreateIn,
  NPEContractListQuery,
  NPEContractOut,
  NPEContractUpdateIn,
  PageResponse,
} from '@/types/api'

const enc = safePathEncode

export const npeContractsApi = baseApi.injectEndpoints({
  endpoints: (b) => ({
    listNPEContracts: b.query<PageResponse<NPEContractOut>, NPEContractListQuery | void>({
      query: (params) => ({
        url: '/purchase/npe-contracts/list',
        params: params ?? {},
      }),
      providesTags: (res) =>
        res
          ? [
              ...res.items.map((it) => ({
                type: 'NPEContract' as const,
                id: it.contract_no,
              })),
              { type: 'NPEContract' as const, id: 'LIST' },
            ]
          : [{ type: 'NPEContract' as const, id: 'LIST' }],
    }),
    /** Defaults cho form New: contract_no (`NPE-XXXXX`) + interest_rate_note
     *  template. Backend mirror Access btnNew_Click trong
     *  `frmPurchaseMainSubFormNPE`: `Suggest_IndexName("consignment_p")` +
     *  `DLookup("value", "public_tmp_add_information", "type='procurement_npe_pr1'")`.
     *
     *  Tag `SUGGEST` được invalidate khi create thành công → Copy/New lần sau
     *  sẽ refetch contract_no mới (parity Access: `Suggest_IndexName` gọi mỗi
     *  lần btnNew/btnCopy_Click). */
    suggestNPEContractNo: b.query<{ contractNo: string; interestRateNote: string }, void>({
      query: () => '/purchase/npe-contracts/suggest-contract-no',
      transformResponse: (r: {
        suggestedNo?: string
        suggested_no?: string
        interestRateNote?: string
        interest_rate_note?: string
      }) => ({
        contractNo: r.suggestedNo ?? r.suggested_no ?? '',
        interestRateNote: r.interestRateNote ?? r.interest_rate_note ?? '',
      }),
      providesTags: [{ type: 'NPEContract' as const, id: 'SUGGEST' }],
    }),
    /** Pre-save check (skill access-suggest): contract_no đã có active row chưa? */
    checkNPEContractNoExists: b.query<{ exists: boolean }, { contractNo: string }>({
      query: ({ contractNo }) => ({
        url: '/purchase/npe-contracts/check-contract-no',
        params: { contractNo },
      }),
      keepUnusedDataFor: 0,
    }),
    getNPEContract: b.query<DataResponse<NPEContractOut>, string>({
      query: (contractNo) => `/purchase/npe-contracts/${enc(contractNo)}`,
      providesTags: (_r, _e, contractNo) => [{ type: 'NPEContract', id: contractNo }],
    }),
    /**
     * Single-row lookup từ v_consignment_p, BYPASS page-level date filter ở
     * `/list` (delivery_time prefs). Dùng autofill dialog NPE Fixation
     * (`supplier_name` + `to_be_fix`): nếu user pick NPE ngoài date range của
     * page, `.find()` trên `useListNPEContractsQuery` trả null → autofill rỗng.
     * `keepUnusedDataFor: 0` + `refetchOnMountOrArgChange: true` ⇒ fresh mỗi
     * lần mở dialog hoặc đổi npe_no. Cùng pattern Futures Allocation detail.
     */
    getNPEContractDetails: b.query<DataResponse<NPEContractOut>, string>({
      query: (contractNo) => `/purchase/npe-contracts/details/${enc(contractNo)}`,
      keepUnusedDataFor: 0,
      providesTags: (_r, _e, contractNo) => [{ type: 'NPEContract', id: contractNo }],
    }),
    createNPEContract: b.mutation<DataResponse<NPEContractOut>, NPEContractCreateIn>({
      query: (body) => ({
        url: '/purchase/npe-contracts/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        { type: 'NPEContract', id: 'LIST' },
        { type: 'NPEContract', id: 'SUGGEST' },
      ],
    }),
    updateNPEContract: b.mutation<
      DataResponse<NPEContractOut>,
      { contract_no: string; body: NPEContractUpdateIn }
    >({
      query: ({ contract_no, body }) => ({
        url: `/purchase/npe-contracts/${enc(contract_no)}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'NPEContract', id: arg.contract_no },
        { type: 'NPEContract', id: 'LIST' },
      ],
    }),
    deleteNPEContract: b.mutation<MessageResponse, string>({
      query: (contractNo) => ({
        url: `/purchase/npe-contracts/${enc(contractNo)}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, contractNo) => [
        { type: 'NPEContract', id: contractNo },
        { type: 'NPEContract', id: 'LIST' },
      ],
    }),
    copyNPEContract: b.mutation<DataResponse<NPEContractOut>, string>({
      query: (contractNo) => ({
        url: `/purchase/npe-contracts/${enc(contractNo)}/copy`,
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'NPEContract', id: 'LIST' }],
    }),
    /** Print PDF — backend trả về base64. */
    printNPEContract: b.query<DataResponse<LocalPPrintResult>, string>({
      query: (contractNo) => `/purchase/npe-contracts/${enc(contractNo)}/print`,
    }),
  }),
})

export const {
  useListNPEContractsQuery,
  useSuggestNPEContractNoQuery,
  useLazySuggestNPEContractNoQuery,
  useLazyCheckNPEContractNoExistsQuery,
  useGetNPEContractQuery,
  useGetNPEContractDetailsQuery,
  useCreateNPEContractMutation,
  useUpdateNPEContractMutation,
  useDeleteNPEContractMutation,
  useCopyNPEContractMutation,
  useLazyPrintNPEContractQuery,
} = npeContractsApi
